import { useCallback, useEffect, useState } from 'react';

import { getMyMenu } from 'services/api/catalogue';

import { AUTH_SESSION_EVENT } from './useOrgBranding';
import { CATALOGUE_VISIBILITY_EVENT } from './useCatalogueVisibility';

// Cached so the sidebar does not flash an empty state on every reload while the
// lookup is in flight. Keyed like the other session lookups.
const CACHE_KEY = 'ayro-my-menu';

const read = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const write = (value) => {
  try {
    if (value) localStorage.setItem(CACHE_KEY, JSON.stringify(value));
    else localStorage.removeItem(CACHE_KEY);
  } catch {
    /* storage unavailable — the menu simply is not cached */
  }
};

/**
 * The navigation this user has been granted, from GET /catalogue/my-menu.
 *
 * Returns null until known. Callers MUST treat null as "not yet known" and fall back
 * to the static per-role menu rather than rendering an empty sidebar — a blank menu on
 * a slow request or a network blip reads as a broken app.
 *
 * Refetches on sign-in and whenever Features and Pages broadcasts a change, so an
 * administrator editing a role does not have to tell people to log out and back in.
 */
export default function useMyMenu() {
  const [data, setData] = useState(() => read());

  const refresh = useCallback(async () => {
    if (!localStorage.getItem('serviceToken')) {
      setData(null);
      write(null);
      return;
    }
    try {
      const res = await getMyMenu();
      setData(res);
      write(res);
    } catch {
      // Leave the previous value in place; see the note above on failing open.
    }
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener(AUTH_SESSION_EVENT, onChange);
    window.addEventListener('storage', onChange);
    window.addEventListener(CATALOGUE_VISIBILITY_EVENT, onChange);
    return () => {
      window.removeEventListener(AUTH_SESSION_EVENT, onChange);
      window.removeEventListener('storage', onChange);
      window.removeEventListener(CATALOGUE_VISIBILITY_EVENT, onChange);
    };
  }, [refresh]);

  return data;
}
